import React from "react";
import { makeStyles } from "@material-ui/core/styles";

import { T } from "../../constants";
import { useUi, cx } from "../../ui";
import Plate from "../../components/Plate";
import Tag from "../../components/Tag";
import VipBadge from "../../components/VipBadge";
import { isUnmatched } from "../../helpers";
import ColumnPanel from "./ColumnPanel";
import { useRowStyles } from "./boardRows";

const useStyles = makeStyles({
  claimed: { fontSize: 11, color: T.mutedFg },
  busy: { fontSize: 11, color: T.mutedFg, fontStyle: "italic" },
});

/*
 * A claimed car is someone's job now, so the queue asks for DETECTED only and
 * a row normally just drops off. This is the belt-and-braces case: a row still
 * in the Apollo cache after a claim must not offer a button the server would
 * refuse.
 */
export function claimStateFor(arrival, disabled) {
  const taken =
    Boolean(arrival.status) &&
    arrival.status !== "DETECTED" &&
    arrival.status !== "DISMISSED";

  return { taken, locked: Boolean(disabled) || taken };
}

export default function ArrivalsQueueColumn({
  arrivals = [],
  onOpenCar,
  onClaim,
  onMatch,
  onDismiss,
  onNewArrival,
  dismissing,
  disabled,
}) {
  const s = useStyles();
  const r = useRowStyles();
  const ui = useUi();

  return (
    <ColumnPanel
      title="Arrivals queue"
      live
      count={arrivals.length}
      noun="waiting to be claimed"
      meta={disabled ? <span className={s.busy}>Finish current intake</span> : null}
      action={
        <button
          type="button"
          className={cx(ui.primaryBtn, r.tiny)}
          onClick={onNewArrival}
          title="A car at the desk the camera did not read"
        >
          + New arrival
        </button>
      }
      empty="No vehicles waiting."
      itemCount={arrivals.length}
    >
      <ul className={r.list}>
        {arrivals.map((arrival) => {
          const { taken, locked } = claimStateFor(arrival, disabled);

          const act = (event, run) => {
            event.stopPropagation();
            run();
          };

          return (
            <li key={arrival.id} data-row>
              <div
                className={r.row}
                role="button"
                tabIndex={0}
                onClick={() => onOpenCar(arrival)}
                onKeyDown={(event) => {
                  if (event.key === "Enter" || event.key === " ") {
                    event.preventDefault();
                    onOpenCar(arrival);
                  }
                }}
              >
                <Plate size={11}>{arrival.plate}</Plate>

                <div className={r.main}>
                  <div className={r.line}>
                    <span className={r.name}>
                      {arrival.guestName || "Unknown vehicle"}
                    </span>
                    {arrival.vip && <VipBadge />}
                    {isUnmatched(arrival) && (
                      <Tag tone="bad" size={9} upper>
                        No booking
                      </Tag>
                    )}
                  </div>
                  {arrival.arrivedGate && (
                    <div className={r.meta}>{arrival.arrivedGate}</div>
                  )}
                </div>

                <div className={r.actions}>
                  {taken ? (
                    <span className={s.claimed}>Claimed</span>
                  ) : (
                    <>
                      {/* Intake needs a booking to run on, so a car that has
                          none is offered the search that finds one rather than
                          a Claim the server would only refuse. */}
                      <button
                        type="button"
                        className={r.claim}
                        disabled={locked}
                        onClick={(event) =>
                          act(event, () =>
                            arrival.bookingId
                              ? onClaim(arrival.id)
                              : onMatch(arrival),
                          )
                        }
                        title={
                          arrival.bookingId
                            ? undefined
                            : "Find the booking this car arrived on"
                        }
                      >
                        {arrival.bookingId ? "Claim" : "Match booking"}
                      </button>
                      <button
                        type="button"
                        className={cx(ui.mutedBtn, r.tiny)}
                        disabled={Boolean(dismissing)}
                        onClick={(event) =>
                          act(event, () => onDismiss(arrival.id))
                        }
                        title="Uber / taxi / drop-off only - remove from valet queue"
                      >
                        Not valet
                      </button>
                    </>
                  )}
                </div>
              </div>
            </li>
          );
        })}
      </ul>
    </ColumnPanel>
  );
}
